import { Link } from 'react-router-dom';
import { Heart, MapPin, Home as HomeIcon, Star, Users, ArrowRight } from 'lucide-react';
import { optimizedImageUrl } from '@/lib/imageOptimizer';
import { AgencyTierBadge, type AgencyTier } from './AgencyTierBadge';
import { cn } from '@/lib/utils';

export interface AgencyShowcaseCardProps {
  id: string;
  slug?: string | null;
  name: string;
  logoUrl?: string | null;
  coverUrl?: string | null;
  city?: string | null;
  propertiesCount?: number;
  membersCount?: number;
  rating?: number;
  reviews?: number;
  tier?: AgencyTier | null;
  highlighted?: boolean;
  className?: string;
}

export function AgencyShowcaseCard({
  id,
  slug,
  name,
  logoUrl,
  coverUrl,
  city,
  propertiesCount = 0,
  membersCount = 0,
  rating = 0,
  reviews = 0,
  tier,
  highlighted,
  className,
}: AgencyShowcaseCardProps) {
  return (
    <Link
      to={`/agencies/${slug || id}`}
      className={cn(
        'group bg-card rounded-xl border overflow-hidden hover:shadow-lg hover:border-primary/40 transition-all flex flex-col',
        highlighted && 'border-primary ring-1 ring-primary/30',
        className
      )}
    >
      <div className="relative">
        <div className="relative h-32 bg-muted overflow-hidden rounded-t-xl">
          {coverUrl ? (
            <img
              src={optimizedImageUrl(coverUrl, { width: 600, height: 320, quality: 70 })}
              alt={name}
              loading="lazy"
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-primary/15 to-primary/5" />
          )}
          {tier && (
            <span className="absolute top-1.5 left-1.5">
              <AgencyTierBadge tier={tier} size="sm" />
            </span>
          )}
          <button
            type="button"
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
            aria-label="Save"
            className="absolute top-2 right-2 h-6 w-6 rounded-full bg-background/95 shadow-sm flex items-center justify-center hover:bg-background"
          >
            <Heart className="h-3 w-3 text-muted-foreground" />
          </button>
        </div>
        <div className="absolute -bottom-4 left-3 h-10 w-10 rounded-lg bg-card shadow-md flex items-center justify-center overflow-hidden">
          {logoUrl ? (
            <img
              src={optimizedImageUrl(logoUrl, { width: 96, height: 96 })}
              alt={name}
              className="h-full w-full object-contain"
            />
          ) : (
            <span className="font-display font-bold text-sm text-primary">
              {name.slice(0, 1).toUpperCase()}
            </span>
          )}
        </div>
      </div>

      <div className="px-3 pt-6 pb-3 flex-1 flex flex-col">
        <h3 className="font-bold text-sm leading-tight truncate">{name}</h3>
        <div className="flex items-center gap-1 text-[11px] text-emerald-600 mt-0.5">
          <MapPin className="h-3 w-3 shrink-0" />
          <span className="truncate">{city || 'Ethiopia'}</span>
        </div>

        <div className="flex items-center justify-between gap-2 mt-2 pt-2 border-t">
          <div className="flex items-center gap-1.5">
            <HomeIcon className="h-3.5 w-3.5 text-muted-foreground" />
            <div className="leading-tight">
              <div className="font-bold text-xs">{propertiesCount.toLocaleString()}</div>
              <div className="text-[9px] text-muted-foreground">Properties</div>
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            <Users className="h-3.5 w-3.5 text-muted-foreground" />
            <div className="leading-tight">
              <div className="font-bold text-xs">{membersCount.toLocaleString()}</div>
              <div className="text-[9px] text-muted-foreground">Members</div>
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            <Star className="h-3.5 w-3.5 text-muted-foreground" />
            <div className="leading-tight">
              <div className="font-bold text-xs">{rating > 0 ? rating.toFixed(1) : '—'}</div>
              <div className="text-[9px] text-muted-foreground">({reviews})</div>
            </div>
          </div>
        </div>

        <div className="mt-2">
          <div className="w-full text-center text-xs font-semibold text-primary border border-primary/30 rounded-md py-1 group-hover:bg-primary/5 transition-colors inline-flex items-center justify-center gap-1">
            View Profile <ArrowRight className="h-3 w-3" />
          </div>
        </div>
      </div>
    </Link>
  );
}
